/**
 * Phase 3.1A — Click-to-Call verify-personal-phone dialog.
 *
 * Two-step OTP flow consuming POST /api/calls/verify-number/start +
 * /confirm. On success, fires `onVerified(phone)` so the caller can
 * resume an in-flight dial action (or just close the dialog from a
 * Settings context).
 */
import React, { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { toast } from 'sonner';
import { Loader2, Phone, ShieldCheck } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const API = process.env.REACT_APP_BACKEND_URL || '';

const normalize = (raw) => {
  // Light client-side cleanup — server enforces E.164.
  const digits = (raw || '').replace(/[^0-9+]/g, '');
  if (!digits) return '';
  if (digits.startsWith('+')) return digits;
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return digits.startsWith('+') ? digits : `+${digits}`;
};

const VerifyPhoneDialog = ({ open, onOpenChange, onVerified, initialPhone = '' }) => {
  const [step, setStep] = useState('phone'); // 'phone' | 'code' | 'done'
  const [phone, setPhone] = useState(initialPhone || '');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setStep('phone');
      setCode('');
      setPhone(initialPhone || '');
    }
  }, [open, initialPhone]);

  const sendCode = async () => {
    const e164 = normalize(phone);
    if (!e164 || e164.length < 11) {
      toast.error('Enter a valid phone number');
      return;
    }
    setSubmitting(true);
    try {
      const token = await getAuth().currentUser?.getIdToken();
      const res = await fetch(`${API}/api/calls/verify-number/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ phone: e164 }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        toast.error(j.detail || 'Could not send verification code');
        return;
      }
      toast.success('Verification code sent — check your phone');
      setPhone(e164);
      setStep('code');
    } catch (err) {
      toast.error('Could not reach the server');
    } finally {
      setSubmitting(false);
    }
  };

  const confirm = async () => {
    const trimmed = (code || '').trim();
    if (trimmed.length < 4) {
      toast.error('Enter the code from your text');
      return;
    }
    setSubmitting(true);
    try {
      const token = await getAuth().currentUser?.getIdToken();
      const res = await fetch(`${API}/api/calls/verify-number/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ phone, code: trimmed }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        toast.error(j.detail || 'Invalid code');
        return;
      }
      toast.success('Phone verified ✓');
      setStep('done');
      onVerified?.(phone);
      // Auto-close shortly after success so callers can resume their action.
      setTimeout(() => onOpenChange?.(false), 600);
    } catch (err) {
      toast.error('Could not reach the server');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="verify-phone-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
            Verify your phone for Click-to-Call
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-500">
            We'll text a 6-digit code so we know it's really your phone. Customers will always see your KRO number — never your personal one.
          </DialogDescription>
        </DialogHeader>

        {step === 'phone' && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="ctc-phone" className="text-xs text-gray-600">Your phone number</Label>
              <div className="relative mt-1">
                <Phone className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  id="ctc-phone"
                  type="tel"
                  autoFocus
                  placeholder="(555) 123-4567"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="pl-8 text-sm"
                  data-testid="verify-phone-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" size="sm" onClick={() => onOpenChange?.(false)}>Cancel</Button>
              <Button
                onClick={sendCode}
                disabled={submitting}
                size="sm"
                data-testid="verify-phone-send-btn"
              >
                {submitting ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : null}
                Send code
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 'code' && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="ctc-code" className="text-xs text-gray-600">
                Code sent to <span className="font-medium text-gray-800">{phone}</span>
              </Label>
              <Input
                id="ctc-code"
                type="text"
                inputMode="numeric"
                autoFocus
                maxLength={6}
                placeholder="123456"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
                className="text-base tracking-[0.4em] text-center mt-1"
                data-testid="verify-phone-code-input"
              />
            </div>
            <DialogFooter className="flex-row justify-between">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setStep('phone')}
                disabled={submitting}
              >
                Use a different number
              </Button>
              <Button
                onClick={confirm}
                disabled={submitting}
                size="sm"
                data-testid="verify-phone-confirm-btn"
              >
                {submitting ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : null}
                Verify
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 'done' && (
          <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="verify-phone-done">
            <ShieldCheck className="w-4 h-4" />
            Verified — you can place calls now.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VerifyPhoneDialog;
